import "@/App.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "./context/AuthContext";
import { ReaderAuthProvider } from "./context/ReaderAuthContext";
import Navigation from "./components/Navigation";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import PublicArticles from "./pages/PublicArticles";
import BookLinks from "./pages/BookLinks";
import Courses from "./pages/Courses";
import MyCertificates from "./pages/MyCertificates";
import Module1 from "./pages/Module1";
import Module2 from "./pages/Module2";
import Module3 from "./pages/Module3";
import Module4 from "./pages/Module4";
import ModuleViewer from "./pages/ModuleViewer";
import Pillars from "./pages/Pillars";
import PaymentSuccess from "./pages/PaymentSuccess";
import CFQMatrix from "./pages/CFQMatrix";
import PromondPublishing from "./pages/PromondPublishing";
import CFQAssessment from "./pages/CFQAssessment";
import Speaking from "./pages/Speaking";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PodcastPage from "./pages/PodcastPage";
import SocialPage from "./pages/SocialPage";
import BioLinks from "./pages/BioLinks";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import VerifyEmail from "./pages/VerifyEmail";
import Organisations from "./pages/Organisations";
import ForSpeakers from "./pages/ForSpeakers";
import ReaderDashboard from "./pages/ReaderDashboard";
import AdminLogin from "./pages/AdminLogin";
import AdminResetPassword from "./pages/AdminResetPassword";
import AcceptInvite from "./pages/AcceptInvite";
import CalendarPage from "./pages/CalendarPage";
import SiteLinksDirectory from "./pages/SiteLinksDirectory";
import AdminLinksManager from "./pages/AdminLinksManager";

// Admin layout and pages
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminArticleEditor from "./pages/admin/AdminArticleEditor";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminSocial from "./pages/admin/AdminSocial";
import AdminPodcast from "./pages/admin/AdminPodcast";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMailing from "./pages/admin/AdminMailing";
import AdminCrawlers from "./pages/admin/AdminCrawlers";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminComments from "./pages/admin/AdminComments";
import AdminReaders from "./pages/admin/AdminReaders";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminContentCalendar from "./pages/admin/AdminContentCalendar";
import AdminBioLinks from "./pages/admin/AdminBioLinks";
import AdminPages from "./pages/admin/AdminPages";
import AdminCFQ from "./pages/admin/AdminCFQ";

// Books Platform Pages (Scalable Structure)
import BooksHubPage from "./pages/books/BooksHubPage";
import Books from "./pages/Books";
import EmailSignature from "./pages/EmailSignature";
import BookHubPage from "./pages/books/cultural-fluency/BookHubPage";
import BookReaderPage from "./pages/books/cultural-fluency/BookReaderPage";
import BookAppsPage from "./pages/books/cultural-fluency/BookAppsPage";
import BookIOSPage from "./pages/books/cultural-fluency/BookIOSPage";
import BookAndroidPage from "./pages/books/cultural-fluency/BookAndroidPage";
import BookLibraryPage from "./pages/books/cultural-fluency/BookLibraryPage";
import BookPaymentSuccessPage from "./pages/books/cultural-fluency/BookPaymentSuccessPage";
import BookPaymentCancelledPage from "./pages/books/cultural-fluency/BookPaymentCancelledPage";

// Legacy pages
import SupportPage from "./pages/SupportPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import ReaderGoogleCallback from "./pages/ReaderGoogleCallback";
import EbookLanding from "./pages/EbookLanding";
import EbookLogin from "./pages/EbookLogin";
import EbookRegister from "./pages/EbookRegister";

// Admin new pages
import AdminSessions from "./pages/admin/AdminSessions";
import AdminSpeakers from "./pages/admin/AdminSpeakers";
import AdminNurture from "./pages/admin/AdminNurture";
import AdminCompose from "./pages/admin/AdminCompose";
import AdminQuoteCards from "./pages/admin/AdminQuoteCards";
import AdminInbox from "./pages/admin/AdminInbox";
import AdminAbout from "./pages/admin/AdminAbout";

// Facilitate (Speaker Workspace)
import FacilitateLogin from "./pages/facilitate/FacilitateLogin";
import FacilitateAcceptInvite from "./pages/facilitate/FacilitateAcceptInvite";
import FacilitateRooms from "./pages/facilitate/FacilitateRooms";
import FacilitateRoom from "./pages/facilitate/FacilitateRoom";
import { FacilitateAuthProvider } from "./context/FacilitateAuthContext";

// Unsubscribe & Confirm
import Unsubscribe from "./pages/Unsubscribe";
import ConfirmSubscription from "./pages/ConfirmSubscription";
import SubscribePage from "./pages/SubscribePage";

// Legacy AdminPanel for backwards compat
import AdminPanel from "./pages/AdminPanel";

const AppContent = () => {
  const location = useLocation();

  // Track page visits — robust: visitor_id (persistent), 30-min sessions, article deduplication, UTMs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/api') || path.startsWith('/facilitate')) return;

    const genId = () =>
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2) + Date.now().toString(36);

    // 1. Visitor ID — persisted across sessions via localStorage
    let vid = localStorage.getItem('_tcl_vid');
    if (!vid) { vid = genId(); localStorage.setItem('_tcl_vid', vid); }

    // 2. Session ID with 30-min inactivity timeout (localStorage)
    const SESSION_MS = 30 * 60 * 1000;
    let sid = localStorage.getItem('_tcl_ses_id');
    const sesTs = parseInt(localStorage.getItem('_tcl_ses_ts') || '0', 10);
    const nowTs = Date.now();
    if (!sid || (nowTs - sesTs) > SESSION_MS) {
      sid = genId();
      localStorage.setItem('_tcl_ses_id', sid);
    }
    localStorage.setItem('_tcl_ses_ts', String(nowTs));

    // 3. Article slug detection + per-session deduplication
    const artMatch = path.match(/^\/articles\/(.+)$/);
    const articleSlug = artMatch ? artMatch[1] : null;
    if (articleSlug) {
      const seenKey = `${sid}:${articleSlug}`;
      const seen = JSON.parse(sessionStorage.getItem('_tcl_seen_art') || '[]');
      if (seen.includes(seenKey)) return; // already counted this article this session
      sessionStorage.setItem('_tcl_seen_art', JSON.stringify([...seen, seenKey]));
    }

    // 4. UTM params — capture first-seen per session; persist for all subsequent pages
    const params = new URLSearchParams(location.search);
    const freshUtm = {};
    ['utm_source','utm_medium','utm_campaign','utm_content','utm_term'].forEach(k => {
      const v = params.get(k); if (v) freshUtm[k] = v;
    });
    if (Object.keys(freshUtm).length > 0) sessionStorage.setItem('_tcl_utm', JSON.stringify(freshUtm));
    const sessionUtm = JSON.parse(sessionStorage.getItem('_tcl_utm') || '{}');

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/track/pageview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        path,
        referrer: document.referrer || null,
        session_id: sid,
        visitor_id: vid,
        article_slug: articleSlug,
        ...sessionUtm,
      }),
    }).catch(() => {});
  }, [location.pathname]);

  // CRITICAL: If returning from Emergent Google OAuth, intercept BEFORE any route logic.
  // AuthCallback exchanges the session_id and stores the reader JWT.
  if (location.hash?.includes('session_id=')) {
    return <ReaderGoogleCallback />;
  }

  // Redirect /#subscribe hash links to the dedicated subscribe page.
  if (location.pathname === '/' && location.hash === '#subscribe') {
    return <Navigate to="/subscribe" replace />;
  }

  const isAdminRoute = location.pathname.startsWith('/admin');
  const isFacilitateRoute = location.pathname.startsWith('/facilitate');
  const isReaderRoute = location.pathname.match(/^\/books\/[^/]+\/read/) || 
                        location.pathname.match(/^\/ebook\/read\//);
  const isAuthPage = ['/signin', '/signup'].includes(location.pathname) ||
                     location.pathname.includes('/payment-success') ||
                     location.pathname.includes('/payment-cancelled') ||
                     location.pathname.startsWith('/ebook/login') ||
                     location.pathname.startsWith('/ebook/register');
  
  const hideNav = isAdminRoute || isFacilitateRoute || isReaderRoute || isAuthPage;
  const hideFooter = hideNav || 
                     location.pathname.includes('/library') ||
                     location.pathname.includes('/apps') ||
                     location.pathname.includes('/ios') ||
                     location.pathname.includes('/android');
  
  return (
    <>
      <ScrollToTop />
      {!hideNav && <Navigation />}
      <Routes>
        {/* Main Site Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/articles" element={<PublicArticles />} />
        <Route path="/articles/:slug" element={<PublicArticles />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/module-success" element={<PaymentSuccess />} />
        <Route path="/my-certificates" element={<MyCertificates />} />
        <Route path="/dashboard" element={<ReaderDashboard />} />
        <Route path="/module-1" element={<ModuleViewer moduleNumber={1} />} />
        <Route path="/module-2" element={<ModuleViewer moduleNumber={2} />} />
        <Route path="/module-3" element={<ModuleViewer moduleNumber={3} />} />
        <Route path="/module-4" element={<ModuleViewer moduleNumber={4} />} />
        <Route path="/pillars" element={<Pillars />} />
        <Route path="/cfq-matrix" element={<CFQMatrix />} />
        <Route path="/promond-publishing" element={<PromondPublishing />} />
        <Route path="/cfq-assessment" element={<CFQAssessment />} />
        <Route path="/speaking" element={<Speaking />} />
        <Route path="/podcast" element={<PodcastPage />} />
        <Route path="/social" element={<SocialPage />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/organisations" element={<Organisations />} />
        <Route path="/for-speakers" element={<ForSpeakers />} />
        <Route path="/bio-links" element={<BioLinks />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/email-signature" element={<EmailSignature />} />
        
        {/* Admin Routes — New Layout */}
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="articles" element={<AdminArticles />} />
          <Route path="articles/new" element={<AdminArticleEditor />} />
          <Route path="articles/:id/edit" element={<AdminArticleEditor />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="social" element={<AdminSocial />} />
          <Route path="podcast" element={<AdminPodcast />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="mailing" element={<AdminMailing />} />
          <Route path="crawlers" element={<AdminCrawlers />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="calendar" element={<AdminContentCalendar />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="links" element={<AdminLinksManager />} />
          <Route path="bio-links" element={<AdminBioLinks />} />
          <Route path="pages" element={<AdminPages />} />
          <Route path="cfq" element={<AdminCFQ />} />
          <Route path="sessions" element={<AdminSessions />} />
          <Route path="speakers" element={<AdminSpeakers />} />
          <Route path="compose" element={<AdminCompose />} />
          <Route path="nurture" element={<AdminNurture />} />
          <Route path="quote-cards" element={<AdminQuoteCards />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="comments" element={<AdminComments />} />
          <Route path="readers" element={<AdminReaders />} />
          <Route path="inbox" element={<AdminInbox />} />
          <Route path="about" element={<AdminAbout />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/forgot-password" element={<AdminResetPassword />} />
        <Route path="/admin/reset-password" element={<AdminResetPassword />} />
        <Route path="/admin/accept-invite" element={<AcceptInvite />} />
        
        {/* Legacy admin route redirect */}
        <Route path="/admin-panel" element={<Navigate to="/admin" replace />} />
        
        {/* Facilitate (Speaker Workspace) */}
        <Route path="/facilitate" element={<FacilitateAuthProvider><FacilitateRooms /></FacilitateAuthProvider>} />
        <Route path="/facilitate/login" element={<FacilitateAuthProvider><FacilitateLogin /></FacilitateAuthProvider>} />
        <Route path="/facilitate/accept-invite" element={<FacilitateAuthProvider><FacilitateAcceptInvite /></FacilitateAuthProvider>} />
        <Route path="/facilitate/room/:sessionCode" element={<FacilitateAuthProvider><FacilitateRoom /></FacilitateAuthProvider>} />
        
        {/* Unsubscribe & Confirm */}
        <Route path="/unsubscribe" element={<Unsubscribe />} />
        <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
        
        {/* Site Links Directory */}
        <Route path="/links" element={<SiteLinksDirectory />} />
        
        {/* Books Platform */}
        <Route path="/books" element={<Books />} />
        <Route path="/books/hub" element={<BooksHubPage />} />
        <Route path="/books/cultural-fluency-without-compromise" element={<BookHubPage />} />
        <Route path="/books/cultural-fluency-without-compromise/read" element={<BookReaderPage />} />
        <Route path="/books/cultural-fluency-without-compromise/read/:chapterId" element={<BookReaderPage />} />
        <Route path="/books/cultural-fluency-without-compromise/apps" element={<BookAppsPage />} />
        <Route path="/books/cultural-fluency-without-compromise/ios" element={<BookIOSPage />} />
        <Route path="/books/cultural-fluency-without-compromise/android" element={<BookAndroidPage />} />
        <Route path="/books/cultural-fluency-without-compromise/library" element={<BookLibraryPage />} />
        <Route path="/books/cultural-fluency-without-compromise/payment-success" element={<BookPaymentSuccessPage />} />
        <Route path="/books/cultural-fluency-without-compromise/payment-cancelled" element={<BookPaymentCancelledPage />} />
        <Route path="/books/cultural-fluency-without-compromise/links" element={<BookLinks />} />
        
        {/* Auth Pages */}
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        
        {/* Legacy Redirects */}
        <Route path="/read" element={<Navigate to="/books/cultural-fluency-without-compromise/read" replace />} />
        <Route path="/apps" element={<Navigate to="/books/cultural-fluency-without-compromise/apps" replace />} />
        <Route path="/ios" element={<Navigate to="/books/cultural-fluency-without-compromise/ios" replace />} />
        <Route path="/android" element={<Navigate to="/books/cultural-fluency-without-compromise/android" replace />} />
        <Route path="/library" element={<Navigate to="/books/cultural-fluency-without-compromise/library" replace />} />
        <Route path="/payment-success" element={<Navigate to="/books/cultural-fluency-without-compromise/payment-success" replace />} />
        <Route path="/payment-cancelled" element={<Navigate to="/books/cultural-fluency-without-compromise/payment-cancelled" replace />} />
        <Route path="/reader/:chapterId" element={<Navigate to="/books/cultural-fluency-without-compromise/read/:chapterId" replace />} />
        
        {/* Legacy eBook Routes */}
        <Route path="/ebook" element={<EbookLanding />} />
        <Route path="/ebook/login" element={<EbookLogin />} />
        <Route path="/ebook/register" element={<EbookRegister />} />
        <Route path="/ebook/library" element={<Navigate to="/books/cultural-fluency-without-compromise/library" replace />} />
        <Route path="/ebook/read/:chapterId" element={<Navigate to="/books/cultural-fluency-without-compromise/read/:chapterId" replace />} />
      </Routes>
      {!hideFooter && <Footer />}
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <AuthProvider>
          <ReaderAuthProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </ReaderAuthProvider>
        </AuthProvider>
      </div>
    </HelmetProvider>
  );
}

export default App;
